import { useTranslation } from 'react-i18next';

import { CheveronSmallLeftOutline, Flex, HStack, IconButton, MaximizeOutline, Text, Tooltip, XOutline } from '@bonusly/design-system';

import { BizyPeopleBrowserBody } from './components/bizy-people-browser-body';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

// Compact two-level navigation menu (BIZY-638). This is the "level 1" surface
// behind the chat: reached via the "‹ Bizy" control in the compact chat header,
// dismissed by the back control or the "Chat with Bizy" row. Mirrors the
// existing history-drawer slide/state (`isHistoryDrawerOpen`) so continuous mode
// gets a menu without a parallel mechanism. Only rendered in compact
// (non-fullscreen) continuous mode with the People Browser enabled — fullscreen
// keeps the People sidebar always visible. Shares its body with the fullscreen
// sidebar via BizyPeopleBrowserBody.

interface BizySidePanelMenuProps {
	isMobileMode?: boolean;
	showFullscreenToggle?: boolean;
}

export function BizySidePanelMenu({ isMobileMode = false, showFullscreenToggle = true }: BizySidePanelMenuProps) {
	const { t } = useTranslation('admin_bizy');
	const closePanel = useBizySidePanelStore((state) => state.close);
	const closeHistoryDrawer = useBizySidePanelStore((state) => state.closeHistoryDrawer);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);

	return (
		<Flex data-component="bizy-side-panel-menu" w="100%" h="100%" minH={0} flexShrink={0} direction="column" bg="white" _dark={{ bg: 'gray.900' }}>
			<Flex align="center" justify="space-between" px={3} py={3} minH="68px" flexShrink={0}>
				<HStack spacing={1} minW={0}>
					<Tooltip label={t('sidePanel.backToChat')} hasArrow={false}>
						<IconButton
							aria-label={t('sidePanel.backToChat')}
							icon={<CheveronSmallLeftOutline boxSize={5} />}
							variant="ghost"
							size="sm"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={closeHistoryDrawer}
						/>
					</Tooltip>
					<Text size="md" fontWeight="semibold" color="text.default" noOfLines={1}>
						{t('menu.title')}
					</Text>
				</HStack>
				<HStack spacing={0}>
					{showFullscreenToggle && !isMobileMode && (
						<Tooltip label={t('sidePanel.fullscreen')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.fullscreen')}
								icon={<MaximizeOutline boxSize={4} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={toggleFullscreen}
							/>
						</Tooltip>
					)}
					<Tooltip label={t('sidePanel.close')} hasArrow={false}>
						<IconButton
							aria-label={t('sidePanel.close')}
							icon={<XOutline boxSize={5} />}
							variant="ghost"
							size="sm"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={() => closePanel()}
						/>
					</Tooltip>
				</HStack>
			</Flex>

			<BizyPeopleBrowserBody />
		</Flex>
	);
}
